import { ALL_LOCATIONS, groupLocationTagsByCategory } from '@solinkcorp/vd-common';
import {
  getLocationTagCategoryAndValue,
  getLocationTagDepth,
  getLocationTagValue,
  getParentCategory,
  getParentTagIdsAndTarget,
  hasParentNode,
} from 'components/SettingsPage/LocationManagement/locationTagUtils';
import { type TagFilter } from 'components/SettingsPage/LocationManagement/types';
import CommonUtil from 'utils/CommonUtil';
import { type VideoDiscoverySite } from 'utils/types';

/**
 * Sorts locations with checked locations at the top
 * It also sorts by the overrideName or name
 * @param locations - Array of locations
 * @param checkedLocationIds - The current checked location ids
 */
export function sortLocationsByChecked(
  locations: readonly VideoDiscoverySite[],
  checkedLocationIds: readonly string[],
) {
  const newLocations = [...locations];
  return newLocations.sort((a: VideoDiscoverySite, b: VideoDiscoverySite) => {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();
    const aIsChecked = checkedLocationIds.includes(a.id);
    const bIsChecked = checkedLocationIds.includes(b.id);

    // Keep all locations to the beginning
    if (a.id === ALL_LOCATIONS) return -1;
    if (b.id === ALL_LOCATIONS) return 1;
    // Sort alphabetically if both are checked
    if ((aIsChecked && bIsChecked) || (!aIsChecked && !bIsChecked))
      return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
    // Ensure checked locations are sorted to the beginning
    if (aIsChecked) return -1;
    if (bIsChecked) return 1;
    // Other wise return 0
    return 0;
  });
}

/**
 * Filters the list of locations with the search string and selected location tag filters
 * @param searchString - The string the user searched for
 * @param locations - Array of locations
 * @param selectedFiltersById - Array of all the locationTags filters that are selected
 */
export function processLocations({
  searchString,
  locations,
  selectedFiltersById,
}: {
  searchString: string;
  locations: readonly VideoDiscoverySite[];
  selectedFiltersById: readonly string[];
}) {
  let processedLocations = [...locations];

  if (searchString) {
    processedLocations = CommonUtil.searchLocation({ searchString, locations });
  }

  if (selectedFiltersById.length) {
    processedLocations = getFilteredLocationsBySelectedFilters({
      selectedFiltersById,
      locations: processedLocations,
    });
  }

  return processedLocations;
}

/**
 * Truncates the location name and location tags with a max character limit if needed.
 * @param locationNames - The string for the location text to display
 * @param selectedLocationTags - Array of all the locationTag ids
 * @param maxCharacterLimit - The max character limit for all the location info.
 * If we have both the locations info and location tags info, they each take half of the max character limit.
 */
export function getLocationInfoToDisplay({
  locationNames,
  locationTags,
  maxCharacterLimit,
  truncatedLocationTagsString,
}: {
  locationNames: string;
  locationTags: string[] | undefined;
  maxCharacterLimit: number;
  truncatedLocationTagsString: string;
}) {
  const locationCharacterLimit = locationNames.length
    ? locationTags?.length
      ? maxCharacterLimit / 2
      : maxCharacterLimit
    : 0;
  const isLocationTruncated = locationNames.length > locationCharacterLimit;
  const locationsToDisplay = `${locationNames.substring(0, locationCharacterLimit)}${
    isLocationTruncated ? '...' : ''
  }`;
  let remainingCharacterLimit = maxCharacterLimit - locationsToDisplay.length;

  let isLocationTagTruncated = false;
  if (locationTags?.length) {
    for (const tag of locationTags || []) {
      // Subtract 2 for the left and right padding of the location tag pill UI
      remainingCharacterLimit -= getLocationTagValue(tag)?.length + 2 || 0;
      if (remainingCharacterLimit < 0) {
        isLocationTagTruncated = true;
        break;
      }
    }
  }
  const locationTagsToDisplay = locationTags?.length
    ? isLocationTagTruncated
      ? [truncatedLocationTagsString]
      : locationTags.map((tag) => getLocationTagValue(tag))
    : undefined;

  return { locationTagsToDisplay, locationsToDisplay };
}

/**
 * Filters the list of locations based on the selected filters
 * @param selectedFiltersById - Array of all the locationTags filters that are selected
 * @param locations - Array of locations
 */
export function getFilteredLocationsBySelectedFilters({
  selectedFiltersById,
  locations,
}: {
  selectedFiltersById: readonly string[];
  locations: readonly VideoDiscoverySite[];
}) {
  const selectedTagsGroupedByParentId = groupLocationTagsByCategory(selectedFiltersById);

  return locations.filter((locationItem: VideoDiscoverySite) => {
    if (!locationItem.locationTags?.length) {
      return false;
    }

    for (const key in selectedTagsGroupedByParentId) {
      const tagsOnSameLevel = selectedTagsGroupedByParentId[key];
      if (!tagsOnSameLevel.some((tag) => locationItem.locationTags?.includes(tag))) {
        return false;
      }
    }

    return true;
  });
}

/**
 * Returns a list of location tag ids which should be displayed
 * A tag will be visible if one of the following is true
 * 1. It's parent tag is selected or has no parent tags
 * 2. The search matches the tag
 * @param locationTaggingFilters - Array of TagFilter object of nodes
 * @param selectedFiltersById - Array of all the locationTags filters that are selected
 * @param searchString - The string the user searched for
 * @param locations - Array of locations
 */
export function getVisibleTagsById({
  locationTaggingFilters,
  searchString,
  selectedFiltersById,
  locationTagSearchMap,
}: {
  locationTaggingFilters: readonly TagFilter[];
  searchString: string;
  selectedFiltersById: readonly string[];
  locationTagSearchMap: LocationTagSearchMap;
}) {
  let ids: string[] = [];
  locationTaggingFilters.forEach((tag) => {
    const matches = searchMatchesTag({ searchString, tag: tag.id, locationTagSearchMap });
    if (matches && searchString) {
      const tagIds = getParentTagIdsAndTarget(tag.id);
      ids = [...ids, ...tagIds];
    } else if (
      !searchString &&
      tag.type === 'tag' &&
      hasParentNode(tag, selectedFiltersById) &&
      !ids.includes(tag.id)
    ) {
      ids.push(tag.id);
    }
  });

  return ids;
}

/**
 * Parses the list of locations into location tag filters, which are used to render the location filters tree
 * @param locations - Array of locations
 */
export function parseLocationTagsIntoFilters(locations: readonly VideoDiscoverySite[]) {
  const locationTags: TagFilter[] = [];
  locations.forEach((location) =>
    location.locationTags?.forEach((locationTag) => {
      const [category, value] = getLocationTagCategoryAndValue(locationTag) || [];
      const depth = getLocationTagDepth(locationTag);
      const parentId = getParentCategory(locationTag);

      locationTags.push({
        id: locationTag,
        label: value,
        parentId: category,
        type: 'tag',
      });

      locationTags.push({
        id: category,
        label: category,
        parentId,
        type: 'category',
        depth,
      });
    }),
  );

  const ids = locationTags.map((o) => o.id);
  return locationTags
    .filter(({ id }, index) => !ids.includes(id, index + 1))
    .sort((a, b) => CommonUtil.sortAlphabeticallyAndNumerically(a['id'], b['id']));
}

export type LocationTagSearchMap = Map<
  string,
  { lowerAddress: string | undefined; lowerName: string }[]
>;

/**
 * The search will match the tag if one of the following conditions is true
 * 1. The search unit is a substring of the location tag
 * 2. The search matches the address OR location name for the target tag
 * @param searchString - The string the user searched for
 * @param tag - Location tag id
 * @param locations - Array of locations
 */
export function searchMatchesTag({
  searchString,
  tag,
  locationTagSearchMap,
}: {
  searchString: string;
  tag: string;
  locationTagSearchMap: LocationTagSearchMap;
}) {
  if (!searchString) {
    return true;
  }

  const searchUnits = searchString.trim().split(/\s+/g);
  const tagValue = getLocationTagValue(tag)?.toLowerCase();
  const locationInfo = locationTagSearchMap.get(tag);

  for (const unit of searchUnits) {
    const lowerUnit = unit.toLowerCase();
    const matchesTag = tagValue?.includes(lowerUnit);
    const matchesLocation = locationInfo?.some(
      (info) => info.lowerAddress?.includes(lowerUnit) || info.lowerName?.includes(lowerUnit),
    );
    if (!matchesTag && !matchesLocation) {
      return false;
    }
  }
  return true;
}

/**
 * Create a map of the location tags to the location names and addresses
 * This is used when we go to search for tags in the filters tree
 * @param locations - VideoDiscoveryLocations
 */
export const createLocationTagSearchMap = (
  locations: readonly VideoDiscoverySite[],
): LocationTagSearchMap => {
  const locationTagSearchMap: LocationTagSearchMap = new Map();
  for (const location of locations) {
    const lowerAddress = location.address?.toLowerCase();
    const lowerName = location.name?.toLowerCase();
    location.locationTags?.forEach((locationTag) => {
      const existingLocationTag = locationTagSearchMap.get(locationTag);
      locationTagSearchMap.set(
        locationTag,
        existingLocationTag
          ? [...existingLocationTag, { lowerAddress, lowerName }]
          : [{ lowerAddress, lowerName }],
      );
    });
  }
  return locationTagSearchMap;
};

/**
 * This sorts the tags, so that ones with type 'category' are at the end
 * @param tagOne - First tag to compare
 * @param tagTwo - Second tag to compare
 */
export function sortLocationTagFilterCategoryToEnd(tagOne: TagFilter, tagTwo: TagFilter) {
  if (tagOne.type === 'category') return 1;
  if (tagTwo.type === 'category') return -1;
  return 0;
}
